import React, { useMemo, useRef, useState } from 'react';
import {
  createColumnHelper,
  PageContainer,
  ZigInput,
  ZigTablePriceLabel,
  ZigSelect,
  ZigTable,
  ZigTableQueryRef,
  ZigTypography,
} from '@zignaly-open/ui';
import {
  useLazyWithdrawalsCsvQuery,
  useWithdrawalsQuery,
} from '../../apis/transfers/api';
import { useTranslation } from 'react-i18next';
import { ValueOrDash } from '../TableUtils/ValueOrDash';
import { Box, Tooltip } from '@mui/material';
import { TransferFilterType, WithdrawalData } from '../../apis/transfers/types';
import { withdrawalStatusColorMap } from './constants';
import { useOperatorOptions, useWithdrawalStatusOptions } from './use';
import Shorten from '../TableUtils/Shorten';
import DateDisplay from '../TableUtils/DateDisplay';
import { isEqual as _isEqual } from 'lodash-es';
import FilterButtons from '../TableUtils/FilterButtons';
import { convertUserInputToNumberFormat } from './utils';
import TableWrap, { TableFilterWrap } from '../TableUtils/TableWrap';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExportButton from '../ExportButton';

const initialFilter: TransferFilterType = {
  userId: '',
  amount: '0',
  operator: 'gte',
  status: '',
};

export default function Withdrawals() {
  const { t } = useTranslation(['transfers', 'common']);

  const [errorInputId, setErrorInputId] = useState<string>('');

  const [filters, setFilters] = useState<TransferFilterType>(initialFilter);
  const [filtersSubmitted, setFiltersSubmitted] =
    useState<TransferFilterType>(filters);

  const ref = useRef<ZigTableQueryRef>(null as ZigTableQueryRef);
  const operators = useOperatorOptions();
  const statusOptions = useWithdrawalStatusOptions();

  const columnHelper = createColumnHelper<WithdrawalData>();
  const columns = useMemo(() => {
    return [
      columnHelper.accessor('createdAt', {
        header: t('table.date'),
        cell: ({ getValue }) => <DateDisplay date={getValue()} />,
      }),
      columnHelper.accessor('email', {
        enableSorting: false,
        header: t('table.user'),
        cell: ({ getValue, row }) => (
          <Box>
            <ZigTypography component={'p'}>{getValue()}</ZigTypography>
            <ZigTypography component={'p'} variant={'body2'}>
              {row.original.userId}
            </ZigTypography>
          </Box>
        ),
      }),
      columnHelper.accessor('amount', {
        header: t('table.amount'),
        cell: ({ row }) => (
          <ZigTablePriceLabel
            value={row.original.amount}
            coin={row.original.currency}
          />
        ),
      }),
      columnHelper.accessor('status', {
        header: t('table.status'),
        enableSorting: false,
        cell: ({ getValue, row }) => (
          <ValueOrDash>
            {getValue() && (
              <ZigTypography
                color={withdrawalStatusColorMap[getValue()]}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {t('withdrawalStatuses.' + getValue())}{' '}
                {!!row.original.error && (
                  <Tooltip title={row.original.error}>
                    <HelpOutlineIcon sx={{ ml: 0.5, height: 16, width: 16 }} />
                  </Tooltip>
                )}
              </ZigTypography>
            )}
          </ValueOrDash>
        ),
      }),
      columnHelper.accessor('fromAddress', {
        enableSorting: false,
        header: t('table.fromAddress'),
        cell: ({ getValue }) => (
          <Box display={'flex'} width={'100%'} justifyContent={'center'}>
            <Shorten
              typographyProps={{ sx: { minWidth: 150 }, variant: 'body2' }}
              breakLines
              text={getValue()}
              width={150}
            />
          </Box>
        ),
      }),
      columnHelper.accessor('toAddress', {
        enableSorting: false,
        header: t('table.toAddress'),
        cell: ({ getValue }) => (
          <Box display={'flex'} width={'100%'} justifyContent={'center'}>
            <Shorten
              typographyProps={{ sx: { minWidth: 150 }, variant: 'body2' }}
              breakLines
              text={getValue()}
              width={150}
            />
          </Box>
        ),
      }),
      columnHelper.accessor('transactionId', {
        enableSorting: false,
        header: t('table.transactionId'),
        cell: ({ getValue }) => (
          <Box display={'flex'} width={'100%'} justifyContent={'center'}>
            <Shorten
              typographyProps={{ variant: 'body2' }}
              breakLines
              text={getValue()}
              width={210}
            />
          </Box>
        ),
      }),
    ];
  }, [filters, ref]);

  return (
    <PageContainer
      style={{
        marginTop: '32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0 50px',
      }}
    >
      <ZigTypography sx={{ mb: 4 }} variant={'h1'}>
        {t('navigation.withdrawals')}
      </ZigTypography>

      <TableFilterWrap
        sx={{
          width: 1000,
        }}
      >
        <ZigInput
          size={'small'}
          label={t('table.userId')}
          placeholder={t('table.userId')}
          value={filters.userId}
          onChange={(e) => {
            setErrorInputId('');
            setFilters((old) => ({ ...old, userId: e.target.value }));
          }}
          error={errorInputId}
        />
        <Box mt={'-2px'}>
          <ZigTypography fontSize={'13px'} lineHeight={'20px'}>
            {t('table.amount')}
          </ZigTypography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              mt: '5px',
              gap: '10px',
            }}
          >
            <ZigSelect
              small
              width={100}
              value={filters.operator}
              onChange={(operator) =>
                setFilters((old) => ({ ...old, operator }))
              }
              options={operators}
            />
            <ZigInput
              size={'small'}
              placeholder={t('table.amount')}
              value={filters.amount}
              onChange={(e) =>
                setFilters((old) => ({
                  ...old,
                  amount: convertUserInputToNumberFormat(
                    e.target.value,
                    old.amount,
                  ),
                }))
              }
            />
          </Box>
        </Box>
        <ZigSelect
          small
          label={t('table.status')}
          value={filters.status}
          onChange={(status) => setFilters((old) => ({ ...old, status }))}
          options={statusOptions}
        />
        <FilterButtons
          onClick={() => {
            if (
              !filters.userId.match(/^[0-9a-fA-F]{24}$/) &&
              filters.userId !== ''
            ) {
              setErrorInputId(t('error.invalid-value'));
              return;
            }
            if (_isEqual(filters, filtersSubmitted)) ref.current?.refetch();
            else setFiltersSubmitted(filters);
          }}
          onClear={() => {
            setFilters({ ...initialFilter });
            setFiltersSubmitted({ ...initialFilter });
          }}
        />
        <ExportButton
          useLazyExportCsvQuery={useLazyWithdrawalsCsvQuery}
          filters={filtersSubmitted}
          fileName={'Withdrawals'}
        />
      </TableFilterWrap>

      <TableWrap>
        <ZigTable
          ref={ref}
          initialState={{
            sorting: [
              {
                id: 'createdAt',
                desc: true,
              },
            ],
          }}
          columns={columns}
          useQuery={useWithdrawalsQuery}
          queryExtraParams={filtersSubmitted}
          enableSortingRemoval={false}
          emptyMessage={t('no-data')}
        />
      </TableWrap>
    </PageContainer>
  );
}
